import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { NoonReportCommentsSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { NoonReportSectionEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { createSectionData, getStringValue, hasInvalidFields, hasMissingValues } from '../../../utils/aolFieldsHelper';
import OdfjellInputComponent, { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { useVesselStore } from '../../../context/VesselContext';
import { CommentsSectionInputElementWrapper } from './CommentsSection.style';
import { ValidatedNoonReport } from '../../../types/ViewModel/ValidatedNoonReport';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';

type VM = {
    comments?: string;
    sectionData?: NoonReportSectionData;
    isInitialized?: boolean;

    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
};

const CommentsSectionComponent: React.FC = () => {
    const { vessel } = useVesselStore();
    const [commentsSectionVM, setCommentsSectionVM] = useState<VM>({});
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const { selectedAolDataItem } = useAolStore();
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (!selectedAolDataItem) return;

        if (!commentsSectionVM.isInitialized) {
            initVM();
        }
    }, [selectedAolDataItem, vessel]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.Comments &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    const isReadOnly = checkIsAolReadOnly(commentsSectionVM?.sectionData);

    const getSectionData = (noonReport?: ValidatedNoonReport): NoonReportSectionData => {
        const sectionData = noonReport?.commentsSection?.sectionData ?? createSectionData(NoonReportSectionEnum.Comments);

        if (sectionData.section !== NoonReportSectionEnum.Comments) {
            sectionData.section = NoonReportSectionEnum.Comments;
        }

        return sectionData;
    };

    const initVM = () => {
        if (!selectedAolDataItem) return;
        const noonReport = selectedAolDataItem.noonReport;
        const vm: VM = {
            comments: getStringValue(noonReport?.commentsSection?.comments),
            sectionData: getSectionData(noonReport),
            isInitialized: true,
        };

        setHasEmptyValues(false);
        setCommentsSectionVM({ ...vm });
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        commentsSectionVM[name] = value ? value : undefined;
        hasInvalidFields(commentsSectionVM, name, changeObject.isValid);
        setCommentsSectionVM({ ...commentsSectionVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const save = () => {
        const ignoreList: string[] = [];

        if (hasMissingValues(commentsSectionVM, ignoreList)) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportCommentsSectionFactory().createBM(commentsSectionVM);
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!commentsSectionVM.hasInvalidFields) {
                saveSection(data);
            }
        }
    };

    return (
        <>
            {selectedAolDataItem && (
                <CommentsSectionInputElementWrapper key={`${selectedAolDataItem.id}_comments_section`}>
                    <OdfjellInputComponent
                        isReadOnly={isReadOnly}
                        type={'textarea'}
                        dataObject={commentsSectionVM}
                        name={'comments'}
                        value={commentsSectionVM.comments}
                        onChange={onInputChange}
                    />
                </CommentsSectionInputElementWrapper>
            )}
        </>
    );
};

export default CommentsSectionComponent;
